import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import { useT } from '../i18n/index.js';
import './Countdown.css';

/**
 * Turn timer primitive. The server owns the deadline (PRD §8) — this only
 * renders the gap between now and that timestamp, so a clock skew or a dropped
 * frame can never change whose turn it is.
 */

const TICK_MS = 100;

export interface TurnProgress {
  /** 0 at the start of the turn, 1 when the deadline has passed. */
  elapsed01: number;
  remainingMs: number;
}

export function useTurnProgress(deadline: number | null, turnMs: number): TurnProgress {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadline === null) return;
    setNow(Date.now());
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      // Nothing left to animate once the turn has expired.
      if (t >= deadline) clearInterval(id);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [deadline]);

  const span = Math.max(1, turnMs);
  if (deadline === null) return { elapsed01: 0, remainingMs: span };
  const remainingMs = Math.max(0, deadline - now);
  return { elapsed01: Math.min(1, Math.max(0, (span - remainingMs) / span)), remainingMs };
}

export interface CountdownProps {
  /** Epoch ms the turn expires; null parks the timer at empty. */
  deadline: number | null;
  turnMs: number;
  variant?: 'ring' | 'bar';
  /** Ring diameter in px. Ignored by the bar. */
  size?: number;
  /** Stroke weight in viewBox units, so it scales with `size`. */
  strokeWidth?: number;
  /** Remaining ms below which the timer goes halina. */
  urgentMs?: number;
  label?: string;
  className?: string;
}

export function Countdown({
  deadline,
  turnMs,
  variant = 'ring',
  size = 56,
  strokeWidth = 6,
  urgentMs = 5_000,
  label,
  className,
}: CountdownProps): JSX.Element {
  const t = useT();
  const { elapsed01, remainingMs } = useTurnProgress(deadline, turnMs);
  const urgent = deadline !== null && remainingMs <= urgentMs;
  const seconds = Math.ceil(remainingMs / 1000);

  const classes = ['countdown', `countdown--${variant}`];
  if (urgent) classes.push('countdown--urgent');
  if (className !== undefined) classes.push(className);

  const aria = {
    role: 'progressbar' as const,
    'aria-label': label ?? t('ui.turn.left'),
    'aria-valuemin': 0,
    'aria-valuemax': Math.round(turnMs / 1000),
    'aria-valuenow': seconds,
    'aria-valuetext': `${seconds}s`,
  };

  if (variant === 'bar') {
    return (
      <div className={classes.join(' ')} {...aria}>
        <div className="countdown__bar-fill" style={{ inlineSize: `${elapsed01 * 100}%` }} />
      </div>
    );
  }

  // Urgency is a shape change as well as a hue change (§7.3), and the width has
  // to be decided here: the inline custom property below outranks any class
  // rule, and `r` must be derived from the same number or the ring clips.
  const width = urgent ? strokeWidth + 2 : strokeWidth;
  const r = (100 - width) / 2;
  const circumference = 2 * Math.PI * r;

  return (
    <span
      className={classes.join(' ')}
      style={{ '--cd-width': width, inlineSize: `${size}px`, blockSize: `${size}px` } as CSSProperties}
      {...aria}
    >
      <svg className="countdown__ring" viewBox="0 0 100 100" width={size} height={size} aria-hidden="true">
        <circle className="countdown__track" cx="50" cy="50" r={r} />
        <circle
          className="countdown__fill"
          cx="50"
          cy="50"
          r={r}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - elapsed01)}
        />
        {/* Sits inside the track so it can never clip against the viewBox edge. */}
        {urgent ? <circle className="countdown__alarm" cx="50" cy="50" r={r - width} /> : null}
      </svg>
    </span>
  );
}
